import React, { useEffect, useRef, useState } from 'react';
import { runE2ETest, getE2ETestStatus, cleanupE2ETest } from '../../Services/arndAPI';

const POLL_INTERVAL_MS = 5000;
const LS_KEY = 'e2e-test-current-run';

const Step = ({ done, label, detail }) => (
  <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, padding: '4px 0' }}>
    <span style={{ fontSize: 18 }}>{done ? '✅' : '⏳'}</span>
    <span style={{ fontWeight: done ? 600 : 400 }}>{label}</span>
    {detail && <span style={{ color: '#666', fontSize: 13 }}>{detail}</span>}
  </div>
);

export default function E2ETest() {
  const [file, setFile] = useState(null);
  const [eventName, setEventName] = useState('');
  const [run, setRun] = useState(null); // response from runE2ETest
  const [status, setStatus] = useState(null); // latest /status response
  const [running, setRunning] = useState(false);
  const [polling, setPolling] = useState(false);
  const [cleaning, setCleaning] = useState(false);
  const [cleaned, setCleaned] = useState(null);
  const [error, setError] = useState('');
  const [lastPolledAt, setLastPolledAt] = useState(null);
  const pollRef = useRef(null);

  // On mount: restore in-flight test from localStorage if present
  useEffect(() => {
    const saved = localStorage.getItem(LS_KEY);
    if (!saved) return;
    try {
      const parsed = JSON.parse(saved);
      if (parsed?.eventId) {
        setRun(parsed);
        setPolling(true);
      }
    } catch {
      localStorage.removeItem(LS_KEY);
    }
  }, []);

  // Polling: when we have an eventId, poll /status until thumbnailed=total or user stops
  useEffect(() => {
    if (!run?.eventId || !polling) return;
    const tick = async () => {
      try {
        const s = await getE2ETestStatus(run.eventId);
        setStatus(s);
        setLastPolledAt(new Date());
        if (s.counts?.total > 0 && s.counts.total === s.counts.thumbnailed) {
          setPolling(false); // pipeline complete; stop polling
        }
      } catch (e) {
        setError(`${e.message} — clearing local state. The test run may have been cleaned up server-side.`);
        setPolling(false);
        localStorage.removeItem(LS_KEY);
      }
    };
    tick();
    pollRef.current = setInterval(tick, POLL_INTERVAL_MS);
    return () => clearInterval(pollRef.current);
  }, [run?.eventId, polling]);

  const handleRun = async () => {
    if (!file) return;
    setError('');
    setRunning(true);
    setRun(null);
    setStatus(null);
    setCleaned(null);
    try {
      const res = await runE2ETest(file, eventName.trim() || undefined);
      setRun(res);
      setPolling(true);
      localStorage.setItem(LS_KEY, JSON.stringify(res));
    } catch (e) {
      setError(e.message);
    } finally {
      setRunning(false);
    }
  };

  const handleClear = () => {
    if (!window.confirm('Clear the local UI state? The test event itself stays on the server. You can resume by hitting status manually or starting a new test.')) return;
    setRun(null);
    setStatus(null);
    setCleaned(null);
    setError('');
    setPolling(false);
    localStorage.removeItem(LS_KEY);
  };

  const handleCleanup = async () => {
    if (!run?.eventId) return;
    if (!window.confirm(
      `Tear down test event "${run.eventName}"? This deletes the Event row (soft), Photos rows, RSVPs, PhotoMatches, S3 objects, and attempts to remove the Rekognition face (SCRUM-274 bug means the face may still persist).`,
    )) return;
    setCleaning(true);
    setError('');
    try {
      const res = await cleanupE2ETest(run.eventId);
      setCleaned(res);
      setPolling(false);
      localStorage.removeItem(LS_KEY);
    } catch (e) {
      setError(e.message);
    } finally {
      setCleaning(false);
    }
  };

  const c = status?.counts || { total: 0, indexed: 0, thumbnailed: 0 };
  const indexed = c.total > 0 && c.indexed === c.total;
  const thumbnailed = c.total > 0 && c.thumbnailed === c.total;

  return (
    <div style={{ padding: '1rem', maxWidth: 760 }}>
      <h1 style={{ marginBottom: 4 }}>E2E Test Console</h1>
      <p style={{ color: '#666', marginTop: 0, fontSize: 13 }}>
        Drive the full photo pipeline against the dev environment.{' '}
        <a href="https://arndtech.atlassian.net/browse/SCRUM-272" target="_blank" rel="noopener noreferrer">SCRUM-272</a>
        .
      </p>

      {error && (
        <div style={{ background: '#fdecea', border: '1px solid #f5c2c0', color: '#a01818', padding: '8px 12px', borderRadius: 4, marginBottom: 12 }}>
          {error}
        </div>
      )}

      <section style={{ border: '1px solid #ddd', borderRadius: 6, padding: 16, marginBottom: 16 }}>
        <h3 style={{ marginTop: 0 }}>1. Setup</h3>
        <div style={{ marginBottom: 12 }}>
          <label style={{ display: 'block', marginBottom: 4, fontWeight: 600 }}>Test photo</label>
          <input
            type="file"
            accept=".jpg,.jpeg,.png,.webp,.bmp,image/jpeg,image/png,image/webp,image/bmp"
            onChange={(e) => setFile(e.target.files?.[0] ?? null)}
            disabled={running || run}
          />
          {file && (
            <span style={{ marginLeft: 8, color: '#666', fontSize: 13 }}>
              {file.name} ({(file.size / 1024 / 1024).toFixed(2)} MB)
            </span>
          )}
        </div>
        <div style={{ marginBottom: 12 }}>
          <label style={{ display: 'block', marginBottom: 4, fontWeight: 600 }}>Event name suffix (optional)</label>
          <input
            type="text"
            value={eventName}
            onChange={(e) => setEventName(e.target.value)}
            placeholder="e.g. smoke-2026-05-25"
            disabled={running || run}
            style={{ width: 300, padding: 4 }}
          />
          <span style={{ marginLeft: 8, color: '#666', fontSize: 13 }}>
            Stored name: <code>E2E-test-{eventName.trim() || '<auto>'}</code>
          </span>
        </div>
        <button onClick={handleRun} disabled={!file || running || run} style={{ padding: '8px 16px' }}>
          {running ? 'Uploading...' : 'Run Test'}
        </button>
      </section>

      {run && (
        <section style={{ border: '1px solid #ddd', borderRadius: 6, padding: 16, marginBottom: 16 }}>
          <h3 style={{ marginTop: 0 }}>2. Pipeline progress</h3>
          <div style={{ fontSize: 13, color: '#666', marginBottom: 12 }}>
            Event <code>{run.eventName}</code> · path <code>{run.eventPath}</code> · id <code>{run.eventId}</code>
          </div>
          <Step done label="Event created in DB" detail={`(id ${run.eventId.slice(0, 8)}…)`} />
          <Step done label="Photo uploaded via FTP" detail={`(${run.ftpFilename})`} />
          <Step done label="S3 PUT + Photos row inserted" detail={status ? `${c.total} photo(s)` : 'awaiting first poll'} />
          <Step
            done={indexed}
            label="Rekognition indexing"
            detail={status ? `${c.indexed}/${c.total} indexed` : ''}
          />
          <Step
            done={thumbnailed}
            label="Thumbnail + medium versions"
            detail={status ? `${c.thumbnailed}/${c.total} thumbnailed` : ''}
          />
          <div style={{ fontSize: 12, color: '#999', marginTop: 8 }}>
            {polling ? `Polling every ${POLL_INTERVAL_MS / 1000}s…` : 'Polling stopped.'}{' '}
            {lastPolledAt && `Last poll: ${lastPolledAt.toLocaleTimeString()}`}
          </div>
        </section>
      )}

      {run && indexed && (
        <section style={{ border: '1px solid #ddd', borderRadius: 6, padding: 16, marginBottom: 16 }}>
          <h3 style={{ marginTop: 0 }}>3. Parent journey (in ArndAPP)</h3>
          <ol style={{ marginTop: 0 }}>
            <li>Open ArndAPP</li>
            <li>Make sure the kid/person in the photo is set up as an Attendee with a face scan</li>
            <li>
              Find event <strong>{run.eventName}</strong> in the Events tab
            </li>
            <li>RSVP that Attendee</li>
            <li>
              The match (<code>event.service.ts:162</code> sync) should fire within ~1-2s. PhotoMatches row appears, OneSignal push delivered.
            </li>
            <li>Open the event gallery in-app — the photo should appear under that Attendee.</li>
          </ol>
        </section>
      )}

      {run && (
        <section style={{ border: '1px solid #ddd', borderRadius: 6, padding: 16 }}>
          <h3 style={{ marginTop: 0 }}>4. Cleanup</h3>
          <p style={{ color: '#666', fontSize: 13, marginTop: 0 }}>
            Optional. Removes the test event + photos + RSVPs + PhotoMatches + S3 objects from dev. The Rekognition face deletion is currently broken — see SCRUM-274. Skip cleanup to preserve test data for inspection or model training.
          </p>
          {cleaned ? (
            <div style={{ background: '#e6f4ea', border: '1px solid #b6dfc1', color: '#1d5d33', padding: '8px 12px', borderRadius: 4 }}>
              Cleaned: photos {cleaned.deletedPhotos}, S3 objects {cleaned.deletedS3Objects}, Rekognition faces {cleaned.deletedRekognitionFaces} (reported), RSVPs {cleaned.deletedEventRSVPS}, PhotoMatches {cleaned.deletedPhotoMatches}, event soft-deleted: {String(cleaned.softDeletedEvent)}.
            </div>
          ) : (
            <div style={{ display: 'flex', gap: 8 }}>
              <button onClick={handleCleanup} disabled={cleaning} style={{ padding: '8px 16px', background: '#fff', border: '1px solid #c00', color: '#c00' }}>
                {cleaning ? 'Tearing down…' : 'Tear Down'}
              </button>
              <button onClick={handleClear} disabled={cleaning} style={{ padding: '8px 16px', background: '#fff', border: '1px solid #999', color: '#333' }}>
                Clear UI state (server-side test untouched)
              </button>
            </div>
          )}
        </section>
      )}
    </div>
  );
}
